import { useRecoilCallback, useRecoilState } from "recoil";
import { DataState, onboardingDataState } from "recoil/onboardingDataAtom";
import { useOnboardingData } from "./useOnboardingData";
import { useMutation } from "@tanstack/react-query";
import { onboardingApi } from "api/onboardingApi";
import { useNavigate } from "react-router-dom";
import { useRef } from "react";
import { captureException } from "../utils/sentry";

interface UseOnboardingPageProps {
  pageKey: string;
  nextPageRoute?: string;
}

export function useOnboardingPage({
  pageKey,
  nextPageRoute,
}: UseOnboardingPageProps) {
  const navigate = useNavigate();
  const [onboardingData] = useRecoilState(onboardingDataState);
  const pageDataHook = useOnboardingData(pageKey);
  const isSavingRef = useRef(false);

  // 1. 서버 저장 Mutation 설정 강화
  const { mutate: savePageData } = useMutation(
    (dataToSave: DataState) => onboardingApi.savePageData(pageKey, dataToSave),
    {
      onSuccess: () => {},
      onError: (error) => {
        console.error(`[${pageKey}] 데이터 저장 최종 실패:`, error);
        captureException(error, {
          tags: { action: "onboarding_save" },
          extra: { pageKey },
        });
      },
    }
  );

  // 2. '다음' 버튼 핸들러에서 await 제거 + 중복 저장 방지 가드 추가
  const handleNext = useRecoilCallback(
    (
      { snapshot } // snapshot을 통해 최신 상태에 접근
    ) =>
      async (dataToSave?: DataState) => {
        // 중복 호출 방지: 저장 중이면 즉시 무시
        if (isSavingRef.current) return;

        let currentPageData: DataState;

        if (dataToSave) {
          // 1. Goal_6처럼 데이터가 직접 전달된 경우, 그 데이터를 사용
          currentPageData = dataToSave;
        } else {
          // 2. 데이터가 전달되지 않은 경우(Goal_2), snapshot에서 최신 상태를 가져옴
          const latestOnboardingData =
            await snapshot.getPromise(onboardingDataState);
          currentPageData = latestOnboardingData[pageKey] ?? {};
        }

        // 저장 시작 플래그 세팅
        isSavingRef.current = true;

        // 이제 currentPageData는 항상 최신 값을 가짐
        savePageData(currentPageData, {
          onSettled: () => {
            // 저장 완료/실패 후 플래그 해제
            isSavingRef.current = false;
          },
        });

        if (nextPageRoute) {
          navigate(nextPageRoute);
        }
      },
    [pageKey, nextPageRoute, navigate, savePageData] // 이 콜백이 의존하는 값들
  );

  return {
    ...pageDataHook,
    handleNext,
  };
}